import React from "react";
import { Plus as AddIcon, MoreHorizontal } from "lucide-react";

import { getBracketMatchLabel } from "../../../../utils/fixtureHelpers";

import TeamSlot from "./TeamSlot";
import InlineEditableName from "./InlineEditableName";

const BracketBlock = ({
  bracketBlock,
  phaseGroup,
  openDropdownPhaseId,
  setOpenDropdownPhaseId,
  onCreateSection,
  onEditBracket,
  onDeleteBracket,
  onAutoAssign,
  // Team slot shared props
  teamSlotProps,
}) => {
  const dropdownId = `bracket-${bracketBlock._id}`;
  const bracketRounds = bracketBlock.rounds || [];
  const isMultiLeg = (bracketBlock.numberOfLegs || 1) > 1;

  // Group fixtures by tieNumber for multi-leg brackets.
  // Returns array of { tieNumber, fixtures: [...] } or original fixtures if single-leg.
  const groupFixtures = (fixtures) => {
    if (!isMultiLeg || !fixtures?.length) return null;
    const tieMap = new Map();
    fixtures.forEach((f) => {
      const key = f.tieNumber ?? f._id;
      if (!tieMap.has(key)) tieMap.set(key, []);
      tieMap.get(key).push(f);
    });
    return Array.from(tieMap.entries()).map(([tieNumber, legs]) => ({
      tieNumber,
      legs: legs.sort((a, b) => (a.leg || 1) - (b.leg || 1)),
      primary: legs[0], // use first leg for team slot data
    }));
  };

  return (
    <div className="flex flex-col items-start gap-[12px] rounded-[20px] bg-[#011224] w-full">
      {/* Bracket header */}
      <div className="flex justify-between items-center w-full">
        <InlineEditableName
          name={bracketBlock.name}
          onRename={(newName) => teamSlotProps.handleRenameBlock?.(phaseGroup.id, bracketBlock._id, newName)}
          canEdit={!!teamSlotProps.canEditFormat}
        />
        <div className="flex items-center">
          {/* <button
            className="bg-transparent border-none p-1 cursor-pointer text-app-text-secondary hover:bg-app-border rounded-full transition-all flex items-center justify-center"
            onClick={() => onCreateSection(phaseGroup)}
            title="Add Section"
          >
            <AddIcon className="w-5 h-5" />
          </button> */}
          <div className="relative">
            <button
              className="bg-transparent border-none p-1 cursor-pointer text-app-text-secondary hover:bg-app-border rounded-full transition-all flex items-center justify-center"
              onClick={() => {
                setOpenDropdownPhaseId(
                  openDropdownPhaseId === dropdownId ? null : dropdownId
                );
              }}
            >
              <MoreHorizontal size={24} />
            </button>

            {openDropdownPhaseId === dropdownId && (
              <div className="absolute top-full right-0 mt-2 w-[200px] bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl overflow-hidden z-[1000]">
                {/* <button
                  className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                  onClick={() => {
                    setOpenDropdownPhaseId(null);
                    onCreateSection(phaseGroup);
                  }}
                >
                  Add Section
                </button> */}
                <button
                  className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                  onClick={() => {
                    setOpenDropdownPhaseId(null);
                    onEditBracket(phaseGroup, bracketBlock);
                  }}
                >
                  Edit bracket
                </button>
                {onAutoAssign && (
                  <button
                    className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                    onClick={() => {
                      setOpenDropdownPhaseId(null);
                      onAutoAssign(phaseGroup.id, bracketBlock);
                    }}
                  >
                    Auto assign teams
                  </button>
                )}
                <button
                  className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-red-400 hover:bg-red-500/20 hover:text-red-300 cursor-pointer transition-colors"
                  onClick={() => {
                    setOpenDropdownPhaseId(null);
                    onDeleteBracket(phaseGroup, bracketBlock);
                  }}
                >
                  Delete bracket
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bracket body - rounds display */}
      <div className="rounded-b-[20px]">
        {bracketRounds.length === 0 ? (
          <div className="mx-4 flex justify-center items-center text-sm text-[#7F8585] py-6 border border-dashed border-[#192634] rounded-lg">
            <span>No rounds yet. Rounds are auto-generated based on team count.</span>
          </div>
        ) : (
          <div
            className="flex gap-[12px] items-start overflow-x-auto"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#1E3A5F #041A30",
              scrollBehavior: "smooth",
            }}
          >
            {bracketRounds.map((round, roundIdx) => (
              <div
                key={round._id || roundIdx}
                className="min-w-[270px] p-3 rounded-2xl gap-0 bg-blue-shade-9"
              >
                {/* Round header */}
                <div className="flex justify-between items-center">
                  <span className="text-app-text-secondary text-[14px] mb-3 font-semibold">
                    {round.name || `Round ${roundIdx + 1}`}
                  </span>
                </div>

                {/* Round fixtures */}
                <div className="flex flex-col gap-[8px]">
                  {round.fixtures && round.fixtures.length > 0 ? (
                    (() => {
                      const grouped = groupFixtures(round.fixtures);
                      const items = grouped || round.fixtures.map((f, i) => ({ primary: f, legs: null, tieNumber: i }));

                      return items.map((item, itemIdx) => {
                        const fixture = item.primary;
                        return (
                          <div
                            key={fixture._id || itemIdx}
                            className="inline-flex flex-col items-start gap-[12px] px-[16px] py-[12px] border border-[#192634] rounded-[20px] bg-[#011224] w-[245px]"
                          >
                            <div className="w-full flex justify-between items-center">
                              <span className="text-[#B2B5AD] text-[14px] font-semibold">
                                {fixture.name || getBracketMatchLabel(round.name, itemIdx + 1)}
                              </span>
                              {item.legs && item.legs.length > 1 && (
                                <span className="text-[#00E7FF] text-[11px] font-medium bg-[#00E7FF]/10 px-2 py-0.5 rounded-full">
                                  {item.legs.length} legs
                                </span>
                              )}
                            </div>
                            <div className="w-full flex flex-col gap-[12px]">
                              {[
                                {
                                  teamId: bracketBlock.teams?.find(
                                    (t) => t.dummySlotNumber === fixture.homeTeamDummySlot
                                  )?.teamId,
                                  name: fixture.homeTeamName,
                                  dummySlot: fixture.homeTeamDummySlot,
                                },
                                {
                                  teamId: bracketBlock.teams?.find(
                                    (t) => t.dummySlotNumber === fixture.awayTeamDummySlot
                                  )?.teamId,
                                  name: fixture.awayTeamName,
                                  dummySlot: fixture.awayTeamDummySlot,
                                },
                              ].map((teamSlot, idx) => {
                                const slotNumber = teamSlot.dummySlot;
                                const teamData = teamSlotProps.categoryTeams?.find(
                                  (t) => t._id === teamSlot.teamId || t.teamId?._id === teamSlot.teamId
                                );
                                return (
                                  <TeamSlot
                                    key={`bracket-team-${bracketBlock._id}-${fixture._id}-${idx}`}
                                    team={teamSlot}
                                    teamName={teamSlot.name || "Unknown Team"}
                                    teamCrest={teamData?.teamId?.crest}
                                    placeholderText={
                                      teamSlotProps.getProgressionRuleText(bracketBlock._id, slotNumber) ||
                                      (roundIdx === 0 ? "Choose the team" : "TBD")
                                    }
                                    blockId={bracketBlock._id}
                                    phaseId={phaseGroup.id}
                                    slotIndex={slotNumber}
                                    assignSlot={slotNumber}
                                    isSelectorOpen={
                                      teamSlotProps.showTeamSelector &&
                                      teamSlotProps.selectedBlockId === bracketBlock._id &&
                                      teamSlotProps.selectedTeamSlot === slotNumber
                                    }
                                    teamSelectorRef={teamSlotProps.teamSelectorRef}
                                    dropDirection="down"
                                    zIndex={99999}
                                    progressionRuleText={teamSlotProps.getProgressionRuleText(
                                      bracketBlock._id,
                                      slotNumber
                                    )}
                                    availableTeams={teamSlotProps.getAvailableTeams()}
                                    onOpenSelector={() =>
                                      teamSlotProps.openTeamSelector(
                                        phaseGroup.id,
                                        bracketBlock._id,
                                        slotNumber
                                      )
                                    }
                                    onAssignTeam={teamSlotProps.handleAssignTeam}
                                    onUnassignTeam={teamSlotProps.handleUnassignTeam}
                                    onRemoveRule={() =>
                                      teamSlotProps.handleRemoveRule(bracketBlock._id, slotNumber)
                                    }
                                    hasExplicitRule={!!teamSlotProps.findRuleForSlot(bracketBlock._id, slotNumber)}
                                    ruleSources={teamSlotProps.getRuleSources?.(phaseGroup.id, bracketBlock._id) || []}
                                    onAssignRule={(source) => teamSlotProps.handleAssignRuleFromClick?.(source, phaseGroup.id, bracketBlock._id, slotNumber)}
                                    DroppableSlot={teamSlotProps.DroppableSlot}
                                    DraggablePosition={teamSlotProps.DraggablePosition}
                                    onDrop={teamSlotProps.handleCreateRuleFromDrag}
                                    phases={teamSlotProps.phases}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <div className="text-[#7F8585] text-sm text-center py-6 border border-dashed border-[#192634] rounded-lg">
                      No matches in this round.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BracketBlock;
